// CustomControlBar.tsx
import * as React from "react";
import {
  DisconnectButton,
  LeaveIcon,
  MediaDeviceMenu,
  TrackToggle,
  useLocalParticipant,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { useTranslation } from "react-i18next";

type Props = {
  className?: string;
  /** Toggle whether the Screen Share control is shown (default: true) */
  enableScreenShare?: boolean;
  /** Called when screen share is toggled */
  onScreenShareChange?: (enabled: boolean, userInitiated: boolean) => void;
  /** Bubble up device errors from the Screen Share toggle */
  onDeviceError?: (args: { source: Track.Source; error: unknown }) => void;
};

export function CustomControlBar({
  className = "",
  enableScreenShare = true,
  onScreenShareChange,
  onDeviceError,
}: Props) {
  const { t } = useTranslation();

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: false });

  const { localParticipant } = useLocalParticipant();
  const isScreenShareEnabled = !!localParticipant?.isScreenShareEnabled;

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled]
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled]
  );

  const browserSupportsScreenSharing =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function";

  return (
    <div className={`lk-control-bar relative ${className}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center justify-center w-full">
        <div className="flex w-full gap-2 sm:w-auto md:justify-center">
          <div className="lk-button-group flex-1 sm:flex-none">
            <TrackToggle
              source={Track.Source.Microphone}
              className="lk-button w-full md:w-auto justify-center"
              onChange={microphoneOnChange}
              showIcon={true}
              title={t("controls.microphone")}
            >
              <span className="hidden md:inline">
                {t("controls.microphone")}
              </span>
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="audioinput"
                aria-label="Select microphone"
                onActiveDeviceChange={(_k, id) =>
                  saveAudioInputDeviceId(id ?? "default")
                }
              />
            </div>
          </div>

          <div className="lk-button-group flex-1 sm:flex-none">
            <TrackToggle
              source={Track.Source.Camera}
              className="lk-button w-full md:w-auto justify-center"
              onChange={cameraOnChange}
              showIcon={true}
              title={t("controls.camera")}
            >
              <span className="hidden md:inline">{t("controls.camera")}</span>
            </TrackToggle>
            <div className="lk-button-group-menu">
              <MediaDeviceMenu
                kind="videoinput"
                aria-label="Select camera"
                onActiveDeviceChange={(_k, id) =>
                  saveVideoInputDeviceId(id ?? "default")
                }
              />
            </div>
          </div>

          {enableScreenShare && browserSupportsScreenSharing && (
            <div className="flex-1 sm:flex-none hidden md:block">
              <TrackToggle
                source={Track.Source.ScreenShare}
                className="lk-button w-full md:w-auto justify-center inline-flex"
                showIcon={true}
                onChange={(enabled, user) =>
                  onScreenShareChange?.(enabled, user)
                }
                onDeviceError={(error) =>
                  onDeviceError?.({ source: Track.Source.ScreenShare, error })
                }
                title={
                  isScreenShareEnabled
                    ? t("controls.screenShareStop")
                    : t("controls.screenShareStart")
                }
              >
                <span className="hidden md:inline whitespace-nowrap">
                  {isScreenShareEnabled
                    ? t("controls.screenShareStop")
                    : t("controls.screenShareStart")}
                </span>
              </TrackToggle>
            </div>
          )}
        </div>

        <DisconnectButton
          className="lk-disconnect-button"
          title={t("controls.leave")}
        >
          <LeaveIcon className="mr-1" />
          {t("controls.leave")}
        </DisconnectButton>
      </div>
    </div>
  );
}
